import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Search, Wallet, TrendingUp, TrendingDown, Activity, Users,
  ArrowRight, Loader2, CheckCircle, AlertTriangle, Sparkles, Globe,
} from 'lucide-react';
import logo from '../assets/images/haythix-logo.png';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import FreeToolsTabs from '../components/FreeToolsTabs';
import Seo from '../components/Seo';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const LABEL_STYLES = {
  Whale:         { bg: 'bg-blue-500/15', text: 'text-blue-400' },
  'Active Trader': { bg: 'bg-emerald-500/15', text: 'text-emerald-400' },
  Sniper:        { bg: 'bg-red-500/15', text: 'text-red-400' },
  Trader:        { bg: 'bg-gray-500/15', text: 'text-gray-400' },
};

export default function WalletProfilerPage() {
  const { address: urlAddress } = useParams();
  const navigate = useNavigate();
  const [inputAddress, setInputAddress] = useState(urlAddress || '');
  const [network, setNetwork] = useState('ethereum');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function analyze() {
    const addr = inputAddress.trim();
    if (!addr) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/public/wallet/${addr}?network=${network}`);
      const data = await res.json();
      if (data.error) { setError(data.error); setProfile(null); }
      else { setProfile(data); navigate(`/wallet/${addr}`, { replace: true }); }
    } catch { setError('Failed to profile wallet. Please try again.'); }
    setLoading(false);
  }

  const summary = profile?.summary || {};
  const perChain = profile?.perChain || [];
  const pendingMetrics = profile?.pendingMetrics || [];
  const isInvalid = profile?.status === 'invalid' || profile?.status === 'unsupported_chain';
  const noActivity = profile?.status === 'no_activity';
  const fmtUsd = (v) => v == null ? '—' : `$${Math.abs(v).toLocaleString()}`;

  const seoPath = urlAddress ? `/wallet/${urlAddress}` : '/wallet';
  const seoTitle = urlAddress
    ? `Wallet profile — ${urlAddress.slice(0, 8)}…${urlAddress.slice(-6)}`
    : 'Free Wallet Profiler — on-chain history & P&L for any address';
  const seoDescription = urlAddress
    ? `Free on-chain wallet profile for ${urlAddress.slice(0, 10)}… on ${network}. Holdings, P&L estimate, activity timeline, risk signals — by haythix.`
    : 'Paste any wallet address (15 chains supported) for a free on-chain profile: holdings across chains, P&L estimate, trading history, risk signals. No sign-up required.';

  return (
    <div className="min-h-screen bg-dark-900">
      <Seo title={seoTitle} description={seoDescription} path={seoPath} />
      <PublicNav />
      <FreeToolsTabs />

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 pt-12 pb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Free Wallet Profiler</h1>
        <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
          Analyze any wallet's trading performance, win rate, and portfolio across 15 chains
        </p>

        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-3 mb-4">
          <input type="text" value={inputAddress} onChange={e => setInputAddress(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && analyze()}
            placeholder="Paste wallet address (0x...)"
            className="flex-1 bg-dark-800 border border-dark-600 rounded-xl px-5 py-3 text-white text-sm font-mono placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
          <select value={network} onChange={e => setNetwork(e.target.value)}
            className="bg-dark-800 border border-dark-600 rounded-xl px-4 py-3 text-white text-sm focus:border-accent-primary focus:outline-none">
            {['ethereum','polygon','bsc','arbitrum','base','avalanche','optimism'].map(n =>
              <option key={n} value={n}>{n.charAt(0).toUpperCase()+n.slice(1)}</option>)}
          </select>
          <button onClick={analyze} disabled={!inputAddress.trim() || loading}
            className="px-8 py-3 rounded-xl text-sm font-semibold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Profile
          </button>
        </div>

        <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
          <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> No signup required</span>
          <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> Multi-chain</span>
          <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> Free forever</span>
        </div>
      </section>

      {error && (
        <div className="max-w-3xl mx-auto px-4 mb-6">
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-sm text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        </div>
      )}

      {/* Results */}
      {profile && (
        <section className="max-w-5xl mx-auto px-4 pb-12 space-y-6">
          {isInvalid && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-2xl px-5 py-6 text-center">
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
              <p className="text-red-300 font-semibold">{profile.error || 'Invalid request'}</p>
            </div>
          )}

          {!isInvalid && (
            <>
              {/* Wallet header + summary */}
              <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-accent-primary/15 flex items-center justify-center">
                    <Wallet className="w-7 h-7 text-accent-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-mono text-sm truncate">{profile.address}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[10px] text-gray-600 capitalize">{profile.chain}</span>
                      {summary.activeDays != null && (
                        <span className="text-[10px] text-emerald-400">{summary.activeDays} active days</span>
                      )}
                      {(summary.activeChains || []).length > 0 && (
                        <span className="text-[10px] text-gray-500">on {summary.activeChains.join(', ')}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-dark-700/50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500">Recent Txs Sampled</p>
                    <p className="text-lg font-bold text-white">{summary.totalTransactionsSampled || 0}</p>
                  </div>
                  <div className="bg-dark-700/50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500">Tokens Held</p>
                    <p className="text-lg font-bold text-white">{summary.uniqueTokensHeld || 0}</p>
                  </div>
                  <div className="bg-dark-700/50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500">Est. Balance</p>
                    <p className="text-lg font-bold text-emerald-400">{fmtUsd(summary.estimatedBalanceUsd)}</p>
                  </div>
                  <div className="bg-dark-700/50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500">Active Days</p>
                    <p className="text-lg font-bold text-white">{summary.activeDays ?? '—'}</p>
                  </div>
                </div>
              </div>

              {noActivity && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl px-5 py-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-300">No on-chain activity found on {profile.chain}</p>
                    <p className="text-xs text-amber-300/80 mt-1 leading-relaxed">
                      This wallet has no transactions on this chain. Try a different chain from the dropdown.
                    </p>
                  </div>
                </div>
              )}

              {/* Per-chain breakdown — real holdings + tokens */}
              {!noActivity && perChain.map(pc => (
                <div key={pc.chain} className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                      {pc.chain} — {pc.txSampleSize || 0} recent txs
                    </h3>
                    {pc.totalUsdEstimate != null && (
                      <span className="text-sm font-bold text-emerald-400">{fmtUsd(pc.totalUsdEstimate)}</span>
                    )}
                  </div>

                  {/* Native + token balances */}
                  <div className="space-y-2 mb-5">
                    {pc.native?.amount != null && pc.native.amount > 0 && (
                      <div className="flex items-center justify-between text-sm bg-dark-700/30 rounded-lg px-3 py-2">
                        <span className="text-white font-medium">{pc.native.symbol}</span>
                        <span className="text-gray-400">{pc.native.amount.toFixed(4)}</span>
                        <span className="text-emerald-400 ml-3 font-mono">{fmtUsd(pc.native.valueUsd)}</span>
                      </div>
                    )}
                    {(pc.tokens || []).map(t => (
                      <div key={t.contractAddress} className="flex items-center justify-between text-sm bg-dark-700/30 rounded-lg px-3 py-2">
                        <span className="text-white font-medium">{t.symbol}</span>
                        <span className="text-gray-400 truncate">{t.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })}</span>
                        <span className="text-emerald-400 ml-3 font-mono">{fmtUsd(t.valueUsd)}</span>
                      </div>
                    ))}
                    {(pc.tokens || []).length === 0 && pc.native?.amount === 0 && (
                      <p className="text-xs text-gray-500 text-center py-2">No token balances on this chain.</p>
                    )}
                  </div>

                  {/* Recent activity */}
                  {(pc.recentTxs || []).length > 0 && (
                    <>
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Recent Transactions</h4>
                      <div className="space-y-1">
                        {pc.recentTxs.slice(0, 5).map((tx, i) => (
                          <div key={i} className="flex items-center gap-3 text-xs bg-dark-700/30 rounded-lg px-3 py-2">
                            <Activity className={`w-3.5 h-3.5 ${tx.isError ? 'text-red-400' : 'text-emerald-400'}`} />
                            <span className="text-gray-500 flex-1 font-mono truncate">{tx.hash.slice(0, 10)}…{tx.hash.slice(-6)}</span>
                            <span className="text-gray-500">{new Date(tx.timestamp).toLocaleDateString()}</span>
                            {tx.isError && <span className="text-red-400 font-bold">FAILED</span>}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}

              {/* Pending metrics — honest about what we can't compute yet */}
              {pendingMetrics.length > 0 && !noActivity && (
                <div className="bg-dark-800/50 rounded-2xl p-5 border border-dashed border-dark-600">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    Deeper Metrics <span className="text-[9px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full">COMING IN v1.1</span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {pendingMetrics.map(pm => (
                      <div key={pm.key} className="bg-dark-700/30 rounded-lg px-3 py-2.5">
                        <p className="text-sm text-white font-medium">{pm.label}</p>
                        <p className="text-[10px] text-amber-400/70 mt-1">{pm.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Conversion CTA */}
          <div className="bg-gradient-to-r from-violet-500/10 to-indigo-500/10 border border-violet-500/30 rounded-2xl p-8 text-center">
            <Sparkles className="w-8 h-8 text-violet-400 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-white mb-2">Want to copy this wallet's trades?</h3>
            <p className="text-gray-400 text-sm mb-5 max-w-md mx-auto">
              haythix lets you automatically mirror any wallet's trades with custom allocation, stop loss, and take profit.
            </p>
            <Link to={`/register?ref=wallet&address=${profile.address}`}
              className="px-6 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r from-violet-500 to-indigo-500 text-white hover:from-violet-400 hover:to-indigo-400 transition-all inline-flex items-center gap-2">
              Start Copy Trading <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Empty state */}
      {!profile && !loading && !error && (
        <section className="max-w-4xl mx-auto px-4 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {[
              { icon: Activity, title: 'Performance Tracking', desc: 'See any wallet\'s win rate, P&L, and trade history' },
              { icon: Globe, title: 'Multi-Chain', desc: 'Analyze wallets across Ethereum, BSC, Polygon, and more' },
              { icon: Users, title: 'Copy Trading', desc: 'Found a top trader? Copy their moves automatically' },
            ].map((f, i) => (
              <div key={i} className="bg-dark-800 rounded-xl p-6 border border-dark-700/50 text-center">
                <f.icon className="w-8 h-8 text-accent-primary mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-1">{f.title}</h3>
                <p className="text-xs text-gray-500">{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/analyze" className="text-sm text-accent-primary hover:underline">Try the Token Health Analyzer →</Link>
          </div>
        </section>
      )}

      {/* SEO FAQ — captures long-tail wallet-profile + on-chain history searches */}
      <section className="max-w-3xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-white mb-6">Frequently asked questions</h2>
        <div className="space-y-4 text-sm">
          <details className="group rounded-xl border border-dark-700/50 bg-dark-800/40 p-4">
            <summary className="font-semibold text-white cursor-pointer">What does the Wallet Profiler show?</summary>
            <p className="text-gray-300 leading-relaxed mt-3">
              Holdings across chains, P&amp;L estimate based on cost basis from on-chain history, trading
              activity timeline, risk signals (interactions with flagged contracts, high-risk approvals), and
              counterparty patterns. Works for any EVM address (0x + 40 hex) on 15 chains plus Solana addresses
              (base58).
            </p>
          </details>
          <details className="group rounded-xl border border-dark-700/50 bg-dark-800/40 p-4">
            <summary className="font-semibold text-white cursor-pointer">Is the Wallet Profiler free?</summary>
            <p className="text-gray-300 leading-relaxed mt-3">
              Yes. Anyone can paste any public wallet address — no account required. The free version shows
              headline holdings + activity. Paid plans add live alerts, copy-trading from that wallet (if it is
              opted-in as a master), and tax-grade lot tracking inside the app.
            </p>
          </details>
          <details className="group rounded-xl border border-dark-700/50 bg-dark-800/40 p-4">
            <summary className="font-semibold text-white cursor-pointer">Can I track a whale wallet?</summary>
            <p className="text-gray-300 leading-relaxed mt-3">
              Yes — the Profiler works on any address regardless of size. Paste the address, pick the chain, see
              the breakdown. For continuous alerts on whale movements, sign up and use the Whale Watcher feature
              inside the app.
            </p>
          </details>
          <details className="group rounded-xl border border-dark-700/50 bg-dark-800/40 p-4">
            <summary className="font-semibold text-white cursor-pointer">Does the Profiler track Solana wallets?</summary>
            <p className="text-gray-300 leading-relaxed mt-3">
              Yes. Switch the chain selector to Solana and paste a base58 address (no 0x prefix). SPL token
              holdings, transaction history, and DEX activity (Jupiter, Raydium, Orca) all surface.
            </p>
          </details>
          <details className="group rounded-xl border border-dark-700/50 bg-dark-800/40 p-4">
            <summary className="font-semibold text-white cursor-pointer">Is profiling a wallet legal?</summary>
            <p className="text-gray-300 leading-relaxed mt-3">
              Yes. On-chain data is public by design — any block explorer (Etherscan, Solscan, Basescan) shows the
              same data. haythix Wallet Profiler just structures it. No private information is exposed.
            </p>
          </details>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
